import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Wand2, Search, Pencil, Lock, Unlock, Trash2, Loader2, LayoutTemplate, Download, FileText, UploadCloud, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CanvasPreview } from "@/components/CanvasPreview";

const CATEGORIES = [
  "Academic", "Admit Card", "Birthday Card", "Bonafide", "Certificate", "Character Certificate",
  "Fee Receipt", "Festival Banner", "General", "Id Card", "Interactive", "Marksheet",
  "Staff Id Card", "Transfer Certificate",
];

export default function Templates() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [del, setDel] = useState(null);
  const [fromFile, setFromFile] = useState(false);
  const [ffFile, setFfFile] = useState(null);
  const [ffName, setFfName] = useState("");
  const [ffCat, setFfCat] = useState("");
  const [ffPreview, setFfPreview] = useState("");
  const [ffBusy, setFfBusy] = useState(false);
  const [exportT, setExportT] = useState(null);
  const [exScope, setExScope] = useState("student");
  const [exStudents, setExStudents] = useState([]);
  const [exClasses, setExClasses] = useState([]);
  const [exStudent, setExStudent] = useState("");
  const [exClass, setExClass] = useState("");
  const [exBusy, setExBusy] = useState(false);

  useEffect(() => {
    if (!exportT) return;
    api.get("/templates/sample-students").then((r) => setExStudents(r.data.students || [])).catch(() => {});
    api.get("/templates/sample-classes").then((r) => setExClasses(r.data.classes || [])).catch(() => {});
  }, [exportT]);

  const dl = async (url, fallback) => {
    setExBusy(true);
    try {
      const r = await api.get(url, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      a.download = m ? m[1] : fallback;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF downloaded");
      setExportT(null);
    } catch (e) { toast.error("Could not generate PDF"); }
    finally { setExBusy(false); }
  };
  const runExport = () => {
    const o = encodeURIComponent(window.location.origin);
    if (exScope === "student") {
      if (!exStudent) { toast.error("Pick a student"); return; }
      dl(`/templates/${exportT.id}/pdf?student_id=${exStudent}&origin=${o}`, "template.pdf");
    } else {
      if (!exClass) { toast.error("Pick a class"); return; }
      dl(`/templates/${exportT.id}/class-pdf?class_id=${exClass}&origin=${o}`, "class.pdf");
    }
  };

  const load = () => {
    setItems(null);
    api.get("/templates").then((r) => setItems(r.data))
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail)); setItems([]); });
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = {};
    (items || []).forEach((t) => { c[t.category] = (c[t.category] || 0) + 1; });
    return c;
  }, [items]);

  const filtered = (items || []).filter((t) => {
    const mq = !q.trim() || t.name.toLowerCase().includes(q.trim().toLowerCase());
    const mc = cat === "All" || t.category === cat;
    return mq && mc;
  });

  const toggleLock = async (t) => {
    try { await api.put(`/templates/${t.id}`, { locked: !t.locked }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/templates/${del.id}`); toast.success("Template deleted"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const pickFile = (f) => {
    setFfFile(f || null);
    setFfPreview("");
    if (f && f.type.startsWith("image/")) setFfPreview(URL.createObjectURL(f));
  };
  const closeFromFile = () => { if (ffBusy) return; setFromFile(false); setFfFile(null); setFfName(""); setFfCat(""); setFfPreview(""); };
  const generateFromFile = async () => {
    if (!ffFile) { toast.error("Choose an image or PDF first"); return; }
    setFfBusy(true);
    const fd = new FormData();
    fd.append("file", ffFile);
    if (ffName.trim()) fd.append("name", ffName.trim());
    if (ffCat) fd.append("category", ffCat);
    try {
      const start = await api.post("/templates/from-file", fd, { timeout: 60000 });
      const jobId = start.data.job_id;
      if (!jobId) throw new Error("no job");
      const deadline = Date.now() + 4 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const st = await api.get(`/templates/from-file/status/${jobId}`);
        if (st.data.status === "done") {
          toast.success(`"${st.data.name}" generated — fine-tune it below`);
          setFromFile(false); setFfFile(null); setFfName(""); setFfCat(""); setFfPreview("");
          nav(`/templates/${st.data.template_id}/edit`);
          return;
        }
        if (st.data.status === "error") {
          toast.error(st.data.error || "Could not generate template");
          return;
        }
      }
      toast.error("Generation is taking too long. Please try again with a clearer file.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not generate template");
    } finally { setFfBusy(false); }
  };

  const Pill = ({ label, n }) => (
    <button onClick={() => setCat(label)} data-testid={`cat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${cat === label ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
      {label}
      <span className={`text-xs rounded-full px-1.5 ${cat === label ? "bg-white/25" : "bg-white text-slate-500"}`}>{n}</span>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Ready Templates</h1>
          <p className="text-slate-500 text-sm mt-1">Platform-wide certificate, ID card &amp; document catalog</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => setFromFile(true)} data-testid="template-from-computer-btn"><UploadCloud size={16} />Template from Computer</Button>
          <Button variant="outline" className="gap-1.5" onClick={() => nav("/templates/new")} data-testid="add-template-btn"><Plus size={16} />Add New Template</Button>
          <Button className="gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600" onClick={() => nav("/templates/designer")} data-testid="canvas-designer-btn"><Wand2 size={16} />Canvas Designer</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name..." className="pl-9" data-testid="template-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill label="All" n={(items || []).length} />
          {CATEGORIES.map((c) => <Pill key={c} label={c} n={counts[c] || 0} />)}
        </div>
      </div>

      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 py-20 text-center text-slate-400" data-testid="templates-empty">
          <LayoutTemplate className="mx-auto mb-3 text-slate-300" size={40} />
          No templates {q || cat !== "All" ? "match your filter" : "yet"}. Click &ldquo;Add New Template&rdquo; to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="templates-grid">
          {filtered.map((t, idx) => (
            <div key={t.id} className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`template-card-${idx}`}>
              <div className="relative aspect-[4/5] bg-white flex items-center justify-center overflow-hidden border-b border-slate-100">
                {t.thumbnail ? (
                  <img src={t.thumbnail} alt={t.name} className="w-full h-full object-cover" />
                ) : (t.canvas && (t.canvas.front || []).length) ? (
                  <CanvasPreview canvas={t.canvas} />
                ) : t.content ? (
                  <div className="absolute inset-0 overflow-hidden bg-white" data-testid={`template-preview-${idx}`}>
                    <div className="origin-top-left" style={{ width: "420px", transform: "scale(0.52)" }}>
                      <div className="p-4 text-[11px] leading-snug text-slate-800 prose max-w-none [&_table]:border-collapse [&_td]:p-1 [&_img]:max-h-10 [&_h2]:text-base [&_h3]:text-sm" dangerouslySetInnerHTML={{ __html: t.content }} />
                    </div>
                    <div className="absolute inset-0" />
                  </div>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center"><LayoutTemplate className="text-white/80" size={48} /></div>
                )}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setExportT(t)} className="w-8 h-8 rounded-full bg-white/90 text-emerald-600 flex items-center justify-center shadow hover:bg-white" title="Export PDF" data-testid={`export-template-${idx}`}><Download size={14} /></button>
                  <button onClick={() => nav(t.canvas ? `/templates/${t.id}/designer` : `/templates/${t.id}/edit`)} className="w-8 h-8 rounded-full bg-white/90 text-indigo-600 flex items-center justify-center shadow hover:bg-white" title="Edit" data-testid={`edit-template-${idx}`}><Pencil size={14} /></button>
                  <button onClick={() => setDel(t)} className="w-8 h-8 rounded-full bg-white/90 text-rose-600 flex items-center justify-center shadow hover:bg-white" title="Delete" data-testid={`delete-template-${idx}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-slate-800 truncate" data-testid={`template-name-${idx}`}>{t.name}</h3>
                  <button onClick={() => toggleLock(t)} className={t.locked ? "text-amber-500" : "text-slate-300 hover:text-slate-500"} title={t.locked ? "Locked" : "Unlocked"} data-testid={`lock-template-${idx}`}>
                    {t.locked ? <Lock size={15} /> : <Unlock size={15} />}
                  </button>
                </div>
                <span className="inline-block mt-2 text-xs font-medium rounded-md bg-slate-100 text-slate-500 px-2 py-0.5" data-testid={`template-category-${idx}`}>{t.category}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!del} onOpenChange={(o) => { if (!o) setDel(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-template-dialog">
          <DialogHeader><DialogTitle>Delete Template</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Delete template <b>{del?.name}</b>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="confirm-delete-template-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!exportT} onOpenChange={(o) => { if (!o) setExportT(null); }}>
        <DialogContent className="max-w-md" data-testid="export-pdf-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText size={18} />Export “{exportT?.name}” as PDF</DialogTitle></DialogHeader>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
            <button onClick={() => setExScope("student")} className={`flex-1 py-2 font-medium ${exScope === "student" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="export-scope-student">A student</button>
            <button onClick={() => setExScope("class")} className={`flex-1 py-2 font-medium ${exScope === "class" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid="export-scope-class">A whole class</button>
          </div>
          {exScope === "student" ? (
            <div className="space-y-1.5">
              <Label>Student</Label>
              <Select value={exStudent} onValueChange={setExStudent}>
                <SelectTrigger data-testid="export-student-select"><SelectValue placeholder="Choose a student" /></SelectTrigger>
                <SelectContent>{exStudents.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.class_name || "—"}{s.school_name ? ` · ${s.school_name}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Class</Label>
              <Select value={exClass} onValueChange={setExClass}>
                <SelectTrigger data-testid="export-class-select"><SelectValue placeholder="Choose a class" /></SelectTrigger>
                <SelectContent>{exClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.student_count} students{c.school_name ? ` · ${c.school_name}` : ""}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-400">One combined PDF — one {exportT?.canvas ? "card" : "page"} per student.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportT(null)}>Cancel</Button>
            <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700" disabled={exBusy} onClick={runExport} data-testid="export-download-btn">{exBusy ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={fromFile} onOpenChange={(o) => { if (!o) closeFromFile(); }}>
        <DialogContent className="max-w-lg" data-testid="template-from-file-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-violet-600" />Template from Computer</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Upload a picture or PDF of an existing document. AI will recreate it as an editable template and fill in live data tags automatically.</p>
          <label className="mt-1 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-violet-400 py-8 cursor-pointer transition-colors" data-testid="template-file-dropzone">
            <UploadCloud size={28} className="text-slate-400" />
            <span className="text-sm text-slate-500">{ffFile ? ffFile.name : "Click to choose an image or PDF"}</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="template-file-input" />
          </label>
          {ffPreview && <img src={ffPreview} alt="preview" className="max-h-48 mx-auto rounded-lg border border-slate-200" data-testid="template-file-preview" />}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Name (optional)</Label>
              <Input value={ffName} onChange={(e) => setFfName(e.target.value)} placeholder="Auto from AI" data-testid="template-file-name" />
            </div>
            <div className="space-y-1">
              <Label>Category (optional)</Label>
              <Select value={ffCat || "auto"} onValueChange={(v) => setFfCat(v === "auto" ? "" : v)}>
                <SelectTrigger data-testid="template-file-category"><SelectValue placeholder="Auto-detect" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeFromFile} disabled={ffBusy}>Cancel</Button>
            <Button className="gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600" onClick={generateFromFile} disabled={ffBusy || !ffFile} data-testid="template-file-generate-btn">
              {ffBusy ? <><Loader2 className="animate-spin" size={16} />Generating…</> : <><Sparkles size={16} />Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
